import { useState } from 'react';
import { X, Info, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Select } from '../ui/Input';
import { Button } from '../ui/Button';
import { fromLocalInputValue, toLocalInputValue } from '../../utils/trips';
import { HISTORY_PRESETS, presetRange } from '../../utils/vehicleHistory';
import type { Vehicle } from '../../types/vehicle';
import type { TripRoute } from '../../types/trip';

interface HistoryQueryFormProps {
  vehicles: Vehicle[];
  route: TripRoute | null;
  isLoading: boolean;
  error: Error | null;
  hasActiveQuery: boolean;
  onQuery: (vehicleId: string, from: string, to: string) => void;
  onClear: () => void;
  /** Valores con los que arranca el formulario (la consulta que viaja en la URL de la página propia). `from`/`to` en ISO. Solo se leen al montar. */
  initial?: { vehicleId: string; from: string; to: string };
}

/**
 * FE-27 — el formulario de consulta del histórico (vehículo + rango + presets
 * + "Consultar"/"Limpiar" + mensajes), extraído de `VehicleHistoryControls`
 * (FE-25/26) para que lo usen las dos carcasas: el panel flotante de desktop y
 * la página propia de mobile.
 *
 * Devuelve un fragmento, no un contenedor: quien lo monta pone la caja (el
 * panel flotante, una hoja inferior…) y este solo aporta los hijos, que ya
 * asumen un contenedor `flex` en columna con `gap`. Guarda su propio estado, así
 * que **quien lo monta debe mantenerlo montado** si no quiere perder lo tecleado
 * (el panel flotante lo oculta con `display: none` en vez de desmontarlo).
 *
 * Igual que el resto de formularios del proyecto: `Select`/`Input
 * type="datetime-local"` de `TripFormModal` (FE-11).
 */
export function HistoryQueryForm({
  vehicles, route, isLoading, error, hasActiveQuery, onQuery, onClear, initial,
}: HistoryQueryFormProps) {
  const [vehicleId, setVehicleId] = useState(initial?.vehicleId ?? '');
  const [from, setFrom] = useState(toLocalInputValue(initial?.from));
  const [to, setTo] = useState(toLocalInputValue(initial?.to));
  const [validationError, setValidationError] = useState<string | null>(null);

  const consultar = () => {
    if (!vehicleId) return setValidationError('Elige un vehículo.');
    const fromIso = fromLocalInputValue(from);
    const toIso = fromLocalInputValue(to);
    if (!fromIso || !toIso) return setValidationError('Elige el inicio y el fin del rango.');
    if (Date.parse(toIso) <= Date.parse(fromIso)) return setValidationError('El fin tiene que ser posterior al inicio.');
    setValidationError(null);
    onQuery(vehicleId, fromIso, toIso);
  };

  const limpiar = () => {
    setVehicleId('');
    setFrom('');
    setTo('');
    setValidationError(null);
    onClear();
  };

  const aplicarPreset = (preset: (typeof HISTORY_PRESETS)[number]['id']) => {
    const { from: presetFrom, to: presetTo } = presetRange(preset);
    setFrom(toLocalInputValue(presetFrom.toISOString()));
    setTo(toLocalInputValue(presetTo.toISOString()));
    setValidationError(null);
  };

  // FE-26 — confirmación visual de la fecha/hora elegida (2026-09-18, pedido
  // del dueño): el input nativo no deja claro que el valor quedó capturado,
  // sobre todo en el picker de algunos navegadores/móviles que no tienen un
  // botón "Aceptar" propio. Se muestra en español, legible, apenas hay valor.
  const confirmacionFecha = (valorLocal: string) => {
    const iso = fromLocalInputValue(valorLocal);
    if (!iso) return null;
    return format(new Date(iso), "d MMM yyyy, HH:mm", { locale: es });
  };
  const confirmacionDesde = confirmacionFecha(from);
  const confirmacionHasta = confirmacionFecha(to);

  const mensajeError = validationError ?? (error ? `No se pudo cargar el histórico: ${error.message}` : null);
  const sinDatos = hasActiveQuery && !isLoading && !error && route && route.points.length === 0;

  return (
    <>
      <div className="monitoring-history__presets">
        {HISTORY_PRESETS.map(p => (
          <button key={p.id} type="button" className="monitoring-history__preset-chip" onClick={() => aplicarPreset(p.id)}>
            {p.label}
          </button>
        ))}
      </div>

      <Select
        label="Vehículo"
        value={vehicleId}
        onChange={e => setVehicleId(e.target.value)}
        options={[
          { value: '', label: 'Selecciona un vehículo…' },
          ...vehicles.map(v => ({ value: v.id, label: `${v.plate}${v.model ? ` · ${v.model}` : ''}` })),
        ]}
      />

      <div>
        <label className="input-label" htmlFor="history-from">Desde</label>
        <div className="input-field">
          <input
            id="history-from"
            type="datetime-local"
            className="input-field__input"
            value={from}
            onChange={e => setFrom(e.target.value)}
          />
        </div>
        {confirmacionDesde && (
          <p className="monitoring-history__date-ok">
            <CheckCircle2 size={12} /> {confirmacionDesde}
          </p>
        )}
      </div>

      <div>
        <label className="input-label" htmlFor="history-to">Hasta</label>
        <div className="input-field">
          <input
            id="history-to"
            type="datetime-local"
            className="input-field__input"
            value={to}
            onChange={e => setTo(e.target.value)}
          />
        </div>
        {confirmacionHasta && (
          <p className="monitoring-history__date-ok">
            <CheckCircle2 size={12} /> {confirmacionHasta}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button size="sm" onClick={consultar} isLoading={isLoading} style={{ flex: 1 }}>
          Consultar
        </Button>
        {hasActiveQuery && (
          <Button size="sm" variant="secondary" leftIcon={<X size={14} />} onClick={limpiar}>
            Limpiar
          </Button>
        )}
      </div>

      {mensajeError && (
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--critical)', fontWeight: 600 }}>{mensajeError}</p>
      )}

      {sinDatos && (
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Info size={12} /> El vehículo no reportó posiciones en ese rango.
        </p>
      )}

      {route?.downsampled && (
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Info size={12} /> El servidor adelgazó el recorrido: algunos tramos se ven rectos porque faltan puntos. Acorta el rango para ver más detalle.
        </p>
      )}
    </>
  );
}
